import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { IconTrash } from '../icons';
import {
  useCharacterSnapshots,
  useCreateSnapshot,
  useDeleteSnapshot,
  type LevelSnapshotDto,
} from '../../lib/characterProgress';
import { CollapsibleSection } from './CollapsibleSection';

const CHART_WIDTH = 600;
const CHART_HEIGHT = 160;
const CHART_PADDING = 20;

/** A dependency-free inline SVG polyline (WP7 spec: acceptable rather than adding a chart library). */
function LevelHistoryChart({ snapshots }: { snapshots: LevelSnapshotDto[] }) {
  if (snapshots.length < 2) {
    return <p className="text-sm text-text-muted">Add at least two snapshots to see a trend line.</p>;
  }
  const levels = snapshots.map((s) => s.level);
  const minLevel = Math.min(...levels);
  const maxLevel = Math.max(...levels);
  const levelRange = Math.max(1, maxLevel - minLevel);
  const times = snapshots.map((s) => new Date(s.takenAt).getTime());
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);
  const timeRange = Math.max(1, maxTime - minTime);

  const points = snapshots.map((s) => {
    const t = new Date(s.takenAt).getTime();
    const x = CHART_PADDING + ((t - minTime) / timeRange) * (CHART_WIDTH - 2 * CHART_PADDING);
    const y =
      CHART_HEIGHT -
      CHART_PADDING -
      ((s.level - minLevel) / levelRange) * (CHART_HEIGHT - 2 * CHART_PADDING);
    return `${x},${y}`;
  });

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="w-full text-accent"
      role="img"
      aria-label={`Level history from ${minLevel} to ${maxLevel}`}
    >
      <polyline points={points.join(' ')} fill="none" stroke="currentColor" strokeWidth={2} />
      {snapshots.map((s, i) => {
        const [x, y] = (points[i] ?? '0,0').split(',');
        return <circle key={s.id} cx={x} cy={y} r={3} fill="currentColor" />;
      })}
      <text x={CHART_PADDING} y={14} className="fill-text-faint text-[10px]">
        {maxLevel}
      </text>
      <text x={CHART_PADDING} y={CHART_HEIGHT - 6} className="fill-text-faint text-[10px]">
        {minLevel}
      </text>
    </svg>
  );
}

export function LevelHistorySection({ characterId }: { characterId: number }) {
  const snapshotsQuery = useCharacterSnapshots(characterId);
  const createSnapshot = useCreateSnapshot(characterId);
  const deleteSnapshot = useDeleteSnapshot(characterId);
  const [level, setLevel] = useState('');
  const [expPercent, setExpPercent] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (snapshotsQuery.isPending) {
    return (
      <CollapsibleSection title="Level history">
        <p className="text-sm text-text-muted">Loading level history…</p>
      </CollapsibleSection>
    );
  }
  if (snapshotsQuery.isError) {
    return (
      <CollapsibleSection title="Level history">
        <p role="alert" className="text-sm text-danger">
          Could not load level history.
        </p>
      </CollapsibleSection>
    );
  }

  const snapshots = snapshotsQuery.data ?? [];

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const parsedLevel = Number(level);
    if (!Number.isInteger(parsedLevel) || parsedLevel < 1 || parsedLevel > 300) {
      setError('Level must be between 1 and 300.');
      return;
    }
    const parsedExp = expPercent.trim() === '' ? null : Number(expPercent);
    if (parsedExp !== null && (!Number.isFinite(parsedExp) || parsedExp < 0 || parsedExp >= 100)) {
      setError('EXP% must be between 0 and 99.99.');
      return;
    }
    createSnapshot.mutate(
      { level: parsedLevel, expPercent: parsedExp },
      {
        onSuccess: () => {
          setLevel('');
          setExpPercent('');
        },
        onError: (err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not save this snapshot.'),
      },
    );
  };

  return (
    <CollapsibleSection title="Level history" subtitle="Manual level/EXP checkpoints">
      <LevelHistoryChart snapshots={snapshots} />

      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-faint">
          Level
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={300}
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="min-h-tap w-24 rounded-lg border border-border-strong bg-bg-elevated-hover px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-faint">
          EXP% (optional)
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={99.99}
            step={0.01}
            value={expPercent}
            onChange={(e) => setExpPercent(e.target.value)}
            className="min-h-tap w-28 rounded-lg border border-border-strong bg-bg-elevated-hover px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
        </label>
        <button
          type="submit"
          disabled={createSnapshot.isPending}
          className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
        >
          {createSnapshot.isPending ? 'Saving…' : 'Add snapshot'}
        </button>
      </form>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {snapshots.length > 0 && (
        <ul className="space-y-1">
          {[...snapshots]
            .reverse()
            .slice(0, 10)
            .map((snapshot) => (
              <li
                key={snapshot.id}
                className="flex min-h-tap items-center justify-between gap-2 rounded-lg border border-border bg-bg-elevated-hover px-3 py-1.5 text-sm"
              >
                <span className="tabular-nums text-text-muted">
                  Lv.{snapshot.level}
                  {snapshot.expPercent !== null ? ` (${snapshot.expPercent}%)` : ''}
                </span>
                <span className="text-xs text-text-faint">{new Date(snapshot.takenAt).toLocaleDateString()}</span>
                <button
                  type="button"
                  onClick={() => deleteSnapshot.mutate(snapshot.id)}
                  aria-label={`Delete snapshot from ${new Date(snapshot.takenAt).toLocaleDateString()}`}
                  className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger"
                >
                  <IconTrash />
                </button>
              </li>
            ))}
        </ul>
      )}
    </CollapsibleSection>
  );
}
